// Wizard público /contrato/assinar/:token (pedido 2026-09-11) — o cliente
// nunca tem sessão GPS Drive aqui. gpsDriveGet/gpsDriveUploadWithFields
// (gpsdrive-client.ts) só anexam Authorization se já existir um token em
// memória e nunca redirecionam sozinhos em 401 — funcionam normalmente sem
// sessão, então reaproveitados como estão.
import { gpsDriveGet, gpsDriveUploadWithFields } from "@/lib/api/gpsdrive-client";
import type { ContractCompany } from "@/services/gpsdrive-contracts.service";

export interface ContractSigningPreview {
  alreadySigned: boolean;
  signedAt: string | null;
  client: {
    name: string;
    cpfCnpj: string | null;
    mobilePhone: string | null;
    address: string | null;
  };
  template: { title: string; content: string } | null;
  signatory: { name: string; role: string } | null;
  // Todos os sócios/testemunhas, na ordem (2026-10-14).
  signatories?: { name: string; role: string }[];
  // Cabeçalho (Administração → Contrato → Dados da empresa, 2026-10-14).
  company?: ContractCompany;
  contractValue: number | null;
  recurrenceValue: number | null;
  // Contrato do motorista: "semanal", "mensal"… (2026-10-26).
  periodLabel?: string | null;
  // Pedido 2026-09-11: o cliente vê placa/modelo/plano dos veículos dele
  // antes de assinar, não só o texto do contrato.
  vehicles: {
    plate: string;
    model: string | null;
    plan: string | null;
    monthlyValue?: number | null;
  }[];
}

export interface FacePhotoVerification {
  // null = empresa não configurou a verificação de rosto — o frontend
  // segue sem checar nada (ver StepPhoto em contrato.assinar.$token.tsx).
  faceDetected: boolean | null;
  reason: string;
}

export interface NtpBrTime {
  time: string | null;
  server: string | null;
  stratum: number | null;
  offsetMs: number | null;
}

export const gpsDriveContractSigningService = {
  // Hora oficial do Brasil (NTP.br) consultada pelo backend — usada no
  // "Certificado de assinaturas gerado em" do PDF do contrato.
  getNtpTime: () => gpsDriveGet<NtpBrTime>("/api/v1/contract-signing/ntp-time"),
  getByToken: (token: string) =>
    gpsDriveGet<ContractSigningPreview>(`/api/v1/contract-signing/${encodeURIComponent(token)}`),
  // Chamado logo após tirar a foto (pedido 2026-09-11) — best-effort, nunca
  // lança de um jeito que trave o wizard: erro de rede aqui é tratado como
  // "sem opinião" pelo caller.
  verifyPhoto: (token: string, photo: File) =>
    gpsDriveUploadWithFields<FacePhotoVerification>(
      `/api/v1/contract-signing/${encodeURIComponent(token)}/verify-photo`,
      photo,
      {},
    ),
  sign: (token: string, input: { signedByName: string; latitude: number; longitude: number; photo: File }) =>
    gpsDriveUploadWithFields<{ signed: boolean }>(
      `/api/v1/contract-signing/${encodeURIComponent(token)}/sign`,
      input.photo,
      {
        signedByName: input.signedByName,
        latitude: String(input.latitude),
        longitude: String(input.longitude),
      },
    ),
};

// Contrato do motorista da Gestão de Frota (2026-10-26) — mesmas rotas
// públicas, prefixo /fleet.
export const fleetContractSigningService = {
  getByToken: (token: string) =>
    gpsDriveGet<ContractSigningPreview>(`/api/v1/contract-signing/fleet/${encodeURIComponent(token)}`),
  verifyPhoto: (token: string, photo: File) =>
    gpsDriveUploadWithFields<FacePhotoVerification>(
      `/api/v1/contract-signing/fleet/${encodeURIComponent(token)}/verify-photo`,
      photo,
      {},
    ),
  sign: (token: string, input: { signedByName: string; latitude: number; longitude: number; photo: File }) =>
    gpsDriveUploadWithFields<{ signed: boolean }>(
      `/api/v1/contract-signing/fleet/${encodeURIComponent(token)}/sign`,
      input.photo,
      {
        signedByName: input.signedByName,
        latitude: String(input.latitude),
        longitude: String(input.longitude),
      },
    ),
};
